/**
 * Outcome Verifier — the sealed AFTER-verdict that closes the loop an
 * OutcomeContract opens.
 *
 * A contract stakes a before-claim ("this follow-up will get a reply within
 * 7 days, $1,497 at stake"). This module REPLAYS reality against that claim
 * and renders one of three honest verdicts:
 *
 *   VERIFIED     — matching evidence landed inside the window.
 *   MISSED       — the window closed and reality never matched the claim.
 *   INCONCLUSIVE — the window has not closed yet (or the measurement is
 *                  impossible with the data provided). Unknown is NEVER
 *                  assumed positive.
 *
 * Deliberately pure: all state (activity rows, a health(t) lookup, the
 * clock) is passed IN. No I/O, no store reads, no writes — the caller seals
 * the verdict into the Decision Ledger via the plain object
 * {@link verdictToLedgerEntry} returns.
 */

import type { OutcomeContract, PredicateKind } from './outcomeContract';

// ---------------------------------------------------------------------------
// Verdict schema
// ---------------------------------------------------------------------------

export type OutcomeVerdict = 'VERIFIED' | 'MISSED' | 'INCONCLUSIVE';

/** The measured after-verdict for one contract. */
export type OutcomeResult = {
  proposalId: string;
  verdict: OutcomeVerdict;
  /** Human-auditable evidence lines (activity ids, measured deltas, timing). */
  evidence: string[];
  /** The measured value for quantitative claims (e.g. the health delta). */
  measuredValue?: number;
  /** Chain hash of the original sealed action, when known — links the loop. */
  originalActionHash?: string;
};

/** Replay context: passed-in state only — the verifier performs no I/O. */
export type VerifyContext = {
  /** Activity rows to replay (ActivityEntry-shaped or looser test doubles). */
  activity: any[];
  /** Health score lookup at an epoch-ms instant; undefined = unmeasurable. */
  healthAt: (t: number) => number | undefined;
  /** The clock, passed in — never read implicitly. */
  now: number;
};

// ---------------------------------------------------------------------------
// Activity matching
// ---------------------------------------------------------------------------

/** Epoch-ms timestamp of an activity row; NaN when it carries none. */
function tsOf(e: any): number {
  if (typeof e?.at === 'number') return e.at;
  if (typeof e?.at === 'string') return Date.parse(e.at);
  if (typeof e?.ts === 'number') return e.ts;
  return NaN;
}

/** Searchable lowercase text of a row (kind + event + summary). */
function textOf(e: any): string {
  return `${e?.kind ?? ''} ${e?.event ?? ''} ${e?.summary ?? ''}`.toLowerCase();
}

/** Does this activity row satisfy an event-shaped predicate kind? */
function matchesPredicate(kind: PredicateKind, e: any): boolean {
  if (e?.kind === kind) return true; // exact predicate-kind tag always matches
  const text = textOf(e);
  switch (kind) {
    case 'reply_received':
      return /repl/.test(text);
    case 'stage_advanced':
      return /stage/.test(text) && /advanc|moved|→/.test(text);
    case 'ticket_closed':
      return /ticket/.test(text) && /clos|done|resolved/.test(text);
    default:
      return false;
  }
}

/** Stable audit label for a matched row. */
function labelOf(e: any, index: number): string {
  const id = typeof e?.id === 'string' && e.id !== '' ? e.id : `#${index}`;
  return `activity:${id}`;
}

// ---------------------------------------------------------------------------
// Verification
// ---------------------------------------------------------------------------

/**
 * Replay passed-in state against one contract and render the verdict.
 * Pure and deterministic: same contract + context → same result.
 *
 *   • Window not yet closed (`now < window.end`) → INCONCLUSIVE, always —
 *     a claim cannot be MISSED while reality still has time to match it.
 *   • Event claims (reply/stage/ticket): VERIFIED when a matching activity
 *     row falls inside [start, end]; MISSED when the closed window holds none.
 *   • 'health_delta': measures healthAt(end) − healthAt(start); VERIFIED when
 *     the delta ≥ target, MISSED when it falls short, INCONCLUSIVE when
 *     either endpoint is unmeasurable (unknown is never assumed positive OR
 *     negative).
 */
export function verifyOutcome(contract: OutcomeContract, ctx: VerifyContext): OutcomeResult {
  const { predicate, proposalId } = contract;
  const { start, end } = predicate.window;

  // The window has not closed: reality still has time. Never a verdict yet.
  if (ctx.now < end) {
    return {
      proposalId,
      verdict: 'INCONCLUSIVE',
      evidence: [`window open until ${end} (now ${ctx.now})`],
    };
  }

  if (predicate.kind === 'health_delta') {
    const before = ctx.healthAt(start);
    const after = ctx.healthAt(end);
    if (before === undefined || after === undefined) {
      return {
        proposalId,
        verdict: 'INCONCLUSIVE',
        evidence: ['health unmeasurable at window boundary'],
      };
    }
    const delta = after - before;
    const target = predicate.target ?? 0;
    const line = `health delta ${delta} vs target ${target} (${before} → ${after})`;
    return {
      proposalId,
      verdict: delta >= target ? 'VERIFIED' : 'MISSED',
      evidence: [line],
      measuredValue: delta,
    };
  }

  // Event-shaped claims: find matching activity inside the closed window.
  const hits: string[] = [];
  ctx.activity.forEach((e, i) => {
    const t = tsOf(e);
    if (!Number.isFinite(t) || t < start || t > end) return;
    if (matchesPredicate(predicate.kind, e)) hits.push(labelOf(e, i));
  });

  if (hits.length > 0) {
    return { proposalId, verdict: 'VERIFIED', evidence: hits, measuredValue: hits.length };
  }
  return {
    proposalId,
    verdict: 'MISSED',
    evidence: [`no ${predicate.kind} evidence in closed window ${start}–${end}`],
  };
}

// ---------------------------------------------------------------------------
// Sealing adapter
// ---------------------------------------------------------------------------

/** The plain ledger-entry payload a verdict seals as. No writes happen here. */
export type OutcomeVerdictEntry = {
  type: 'outcome_verdict';
  /** Hash of the original sealed action this verdict answers. */
  refHash: string;
  verdict: OutcomeVerdict;
  evidence: string[];
  /** Measured value carried when the claim was quantitative. */
  value?: number;
};

/**
 * Shape a verdict into the plain object the Decision Ledger seals — linked
 * back to the original action's chain hash so the before-claim and the
 * after-verdict are one auditable loop. Pure: builds the object, writes
 * nothing; the caller appends it via the existing ledger API.
 */
export function verdictToLedgerEntry(
  result: OutcomeResult,
  originalHash: string,
): OutcomeVerdictEntry {
  const entry: OutcomeVerdictEntry = {
    type: 'outcome_verdict',
    refHash: originalHash,
    verdict: result.verdict,
    evidence: [...result.evidence],
  };
  if (result.measuredValue !== undefined) entry.value = result.measuredValue;
  return entry;
}
